import { format, parse } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { useUserContext } from '../../context/UserContext';
import { useUpdateUser } from '../../hooks/user/useUpdateUser';
import { Formik, FormikProps } from 'formik';
import Form from '../../components/form/Form';
import * as Yup from 'yup';
import FormInput from '../../components/form/FormInput';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { MyProfileSettingsParamList } from '../../navigation/MyProfileSettingsNav';
import FormButton from '../../components/form/FormButton';
import FormDateInput from '../../components/form/FormDateInput';
import FormSelect from '../../components/form/FormSelect';
import Spacer from '../../components/Spacer';
import { Platform } from 'react-native';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import FormMaskedInput from '../../components/form/FormMaskedInput';
import CustomToggleSwitch from '../../components/CustomToggleSwitch';
import elementIDs from '../../../elementIDs';
import { UserBiologicalSex, UserUnitSystem } from '../../models/User';
import { usStatesOptions } from '../../models/UsStateOptions';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const passwordMinLength = 6;
const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/;
const passwordRegexErrorMessage =
  'Need at least an uppercase and a lowercase letter, number, and a special character.';

const phoneRegExp =
  /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;

const StyledScrollView = styled(KeyboardAwareScrollView)`
  background-color: #fdfbf7;
  padding: 32px 23px 32px 23px;
  flex: 1;
`;

const StyledContainer = styled.View`
  flex: 1;
  width: 100%;
  align-self: center;
  max-width: 768px;
  margin-top: ${Platform.OS === 'web' ? 0 : 10}px;
`;

const StyledSubtitle = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 14px;
  line-height: 18px;
  color: #141027;
`;

const StyledButtonContainer = styled.View`
  position: absolute;
  bottom: 0;
  left: -10px;
  right: -10px;
`;

const StyledSettingsContainer = styled.View<{ disabled?: boolean }>`
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  flex-direction: row;
  min-width: 200px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const StyledLabel = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 18px;
  line-height: 23px;
  color: #232f55;
`;

const MyProfileEditInfoScreen: React.FC = () => {
  const formikRef = React.useRef<FormikProps<any> | null>();
  const route =
    useRoute<RouteProp<MyProfileSettingsParamList, 'MyProfileEditInfo'>>();
  const profileInfoType = route.params.profileInfoType;
  const { user, setUser } = useUserContext();
  const {
    updateUserError,
    updateUser,
    updatedUser,
    clearUpdatedUser,
    updatingUser
  } = useUpdateUser(true);
  const navigation = useNavigation();
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(
    user?.phoneNumber?.match(phoneRegExp) || false
  );
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<string>('');

  useEffect(() => {
    if (updateUserError) {
      if (profileInfoType === 'email') {
        formikRef.current?.setFieldError('email', updateUserError.message);
        formikRef.current?.setSubmitting(false);
      } else {
        formikRef.current?.setFieldError(
          'currentPassword',
          'Incorrect password'
        );
        formikRef.current?.setSubmitting(false);
      }
    }
  }, [profileInfoType, updateUserError]);

  const initialValues = useMemo(() => {
    let values = {} as any;
    if (profileInfoType === 'name') {
      values = {
        firstName: user?.firstName,
        lastName: user?.lastName
      };
    } else if (profileInfoType === 'email') {
      values = { email: user?.email };
    } else if (profileInfoType === 'birthDate') {
      values = {
        birthDate: user?.birthDate
          ? format(
              parse(user?.birthDate, 'yyyy-MM-dd', new Date()),
              'MM/dd/yyyy'
            )
          : ''
      };
    } else if (profileInfoType === 'state') {
      values = {
        state: user?.state
      };
    } else if (profileInfoType === 'phoneNumber') {
      values = {
        phoneNumber: user?.phoneNumber,
        phoneNumberCode: user?.phoneNumberCode,
        notifyForMessages: user?.notifyForMessages,
        notifyForStudy: user?.notifyForStudy
      };
    } else if (profileInfoType === 'unitSystem') {
      values = {
        unitSystem: user?.unitSystem
      };
    } else if (profileInfoType === 'biologicalSex') {
      values = {
        biologicalSex: user?.biologicalSex
      };
    }
    return values;
  }, [profileInfoType, user]);

  useEffect(() => {
    let title = 'Edit Name';
    if (profileInfoType === 'email') {
      title = 'Edit Email';
    } else if (profileInfoType === 'password') {
      title = 'Change Password';
    } else if (profileInfoType === 'birthDate') {
      title = 'Edit Birthday';
    } else if (profileInfoType === 'state') {
      title = 'State Of Residence';
    } else if (profileInfoType === 'phoneNumber') {
      title = 'Edit Phone Number';
    } else if (profileInfoType === 'unitSystem') {
      title = 'Edit Unit System';
    } else if (profileInfoType === 'biologicalSex') {
      title = 'Edit Biological Sex';
    }
    navigation.setOptions({ title });
  }, [navigation, profileInfoType]);

  useEffect(() => {
    if (updatedUser) {
      clearUpdatedUser();
      setUser(updatedUser);
      navigation.goBack();
    }
  }, [clearUpdatedUser, navigation, setUser, updatedUser]);

  return (
    <StyledScrollView contentContainerStyle={{ flexGrow: 1 }}>
      <StyledContainer>
        <Formik
          enableReinitialize
          innerRef={formik => (formikRef.current = formik)}
          validateOnMount={true}
          initialValues={initialValues}
          validationSchema={Yup.object({
            ...(profileInfoType === 'name' && {
              firstName: Yup.string().required('First name is required.'),
              lastName: Yup.string().required('Last name is required.')
            }),
            ...(profileInfoType === 'email' && {
              email: Yup.string().email().required('Email is required.')
            }),
            ...(profileInfoType === 'password' && {
              currentPassword: Yup.string().required(
                'Current password is required'
              ),
              password: Yup.string()
                .matches(passwordRegex, passwordRegexErrorMessage)
                .min(
                  passwordMinLength,
                  `Minimum ${passwordMinLength} characters.`
                )
                .required('Password is required'),
              confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'New passwords must match')
                .required('Password is required')
            }),
            ...(profileInfoType === 'birthDate' && {
              birthDate: Yup.date()
                .transform((value, originalValue) => {
                  // transform value to yyyy-MM-dd before validating
                  if (typeof originalValue === 'string') {
                    const dateArr = originalValue.split('/');
                    if (dateArr.length === 3) {
                      if (dateArr[2].length === 4) {
                        return new Date(
                          dateArr[2] + '-' + dateArr[0] + '-' + dateArr[1]
                        );
                      }
                    }
                  }
                  return originalValue;
                })
                .typeError('Invalid date')
                .max(new Date(), 'Please specify a date in the past.')
                .required('Date of birth is required.')
            }),
            ...(profileInfoType === 'state' && {
              state: Yup.string().required()
            }),
            ...(profileInfoType === 'unitSystem' && {
              unitSystem: Yup.string().required()
            })
          })}
          onSubmit={formValues => {
            // if (formikRef.current?.isValidating) return;
            if (profileInfoType === 'phoneNumber') {
              if (!isPhoneNumberValid) {
                formValues.notifyForMessages = false;
                formValues.notifyForStudy = false;
              }
              if (selectedCountryInfo) {
                const splittedCountryInfo = selectedCountryInfo.split(' ');
                const dialCode = splittedCountryInfo.slice(-1)[0];
                formValues.phoneNumber = `${dialCode}${formValues.phoneNumber}`;
                formValues.phoneNumberCode = splittedCountryInfo[0];
                setSelectedCountryInfo('');
              }
            }
            updateUser({ ...formValues });
          }}
        >
          {({ setFieldValue, values, dirty }) => (
            <Form>
              {profileInfoType === 'name' && (
                <>
                  <FormInput
                    testID={elementIDs.firstNameFieldID}
                    returnKeyType='next'
                    name='firstName'
                    placeholder='First Name'
                  />
                  <FormInput
                    testID={elementIDs.lastNameFieldID}
                    returnKeyType='done'
                    name='lastName'
                    placeholder='Last Name'
                  />
                </>
              )}
              {profileInfoType === 'email' && (
                <FormInput
                  testID={elementIDs.emailFieldID}
                  returnKeyType='done'
                  name='email'
                  placeholder='Email'
                />
              )}
              {profileInfoType === 'password' && (
                <>
                  <StyledSubtitle>
                    Your password must be at least 6 characters and should
                    include a combination of numbers, letters, and special
                    characters (!$@%).
                  </StyledSubtitle>
                  <Spacer size={4} />
                  <FormInput
                    testID={elementIDs.currentPasswordFieldID}
                    returnKeyType='next'
                    name='currentPassword'
                    placeholder='Current password'
                    secureTextEntry
                  />
                  <FormInput
                    testID={elementIDs.newPasswordFieldID}
                    returnKeyType='next'
                    name='password'
                    placeholder='New password'
                    secureTextEntry
                  />
                  <FormInput
                    testID={elementIDs.confirmPasswordFieldID}
                    returnKeyType='done'
                    name='confirmPassword'
                    placeholder='New password, again'
                    secureTextEntry
                  />
                </>
              )}
              {profileInfoType === 'birthDate' && (
                <FormDateInput
                  testID={elementIDs.birthdayDateFieldID}
                  autoSubmit={Platform.OS !== 'web'}
                  showPlaceHolder
                  name='birthDate'
                  birthDate
                />
              )}
              {profileInfoType === 'state' && (
                <>
                  <StyledSubtitle>
                    Sharing your state allows us to recommend studies in your
                    area.
                  </StyledSubtitle>
                  <Spacer size={4} />
                  <FormSelect
                    testID={elementIDs.stateFieldID}
                    placeholder='State of Residence'
                    name='state'
                    options={usStatesOptions}
                  />
                </>
              )}
              {(profileInfoType !== 'birthDate' || Platform.OS === 'web') && (
                <StyledButtonContainer>
                  <FormButton
                    testID={elementIDs.saveButtonID}
                    disabled={
                      profileInfoType === 'phoneNumber' && !isPhoneNumberValid
                    }
                    color='secondary'
                  >
                    Save
                  </FormButton>
                </StyledButtonContainer>
              )}
              {profileInfoType === 'phoneNumber' && (
                <>
                  <FormMaskedInput
                    testID={elementIDs.phoneFieldID}
                    name='phoneNumber'
                    placeholder='Phone Number'
                    inputType='phone'
                    isValid={(isValid, countryInfo) => {
                      if (isValid) {
                        setIsPhoneNumberValid(true);
                        setFieldValue('notifyForMessages', true);
                        setFieldValue('notifyForStudy', true);
                      } else {
                        setIsPhoneNumberValid(false);
                      }
                      if (countryInfo) {
                        setSelectedCountryInfo(countryInfo);
                      }
                    }}
                  />
                  <Spacer />
                  <StyledSettingsContainer disabled={!isPhoneNumberValid}>
                    <StyledLabel testID={elementIDs.textMessagesLabelID}>
                      Text for Messages
                    </StyledLabel>
                    <CustomToggleSwitch
                      testID={elementIDs.textMessagesToggleID}
                      disabled={!isPhoneNumberValid}
                      value={values['notifyForMessages']}
                      onToggle={() => {
                        setFieldValue(
                          'notifyForMessages',
                          !values['notifyForMessages']
                        );
                      }}
                    />
                  </StyledSettingsContainer>
                  <Spacer />
                  <StyledSettingsContainer disabled={!isPhoneNumberValid}>
                    <StyledLabel>Text for Study Notifications</StyledLabel>
                    <CustomToggleSwitch
                      testID={elementIDs.textNotificationsToggleID}
                      disabled={!isPhoneNumberValid}
                      value={values['notifyForStudy']}
                      onToggle={() => {
                        setFieldValue(
                          'notifyForStudy',
                          !values['notifyForStudy']
                        );
                      }}
                    />
                  </StyledSettingsContainer>
                </>
              )}
              {profileInfoType === 'unitSystem' && (
                <FormSelect
                  placeholder='Unit System'
                  name='unitSystem'
                  options={Object.keys(UserUnitSystem)}
                />
              )}
              {profileInfoType === 'biologicalSex' && (
                <FormSelect
                  placeholder='Biological Sex'
                  name='biologicalSex'
                  options={Object.keys(UserBiologicalSex)}
                />
              )}
            </Form>
          )}
        </Formik>
      </StyledContainer>
      {updatingUser && <ActivityIndicatorScreen modal />}
    </StyledScrollView>
  );
};

export default MyProfileEditInfoScreen;
